import { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Sidebar from '../components/Sidebar';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Loader2,
  ArrowLeft,
  Mail,
  Phone,
  StickyNote,
  ImageIcon,
  Images,
  Package,
  Calendar,
  Hash,
  MessageSquare,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusBadgeVariant = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'done':
    case 'delivered':
      return 'default';
    case 'pending':
    case 'in_progress':
    case 'processing':
      return 'secondary';
    case 'cancelled':
    case 'rejected':
      return 'destructive';
    default:
      return 'outline';
  }
};

const statusLabel = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return 'Zakonczone';
    case 'done': return 'Gotowe';
    case 'delivered': return 'Dostarczone';
    case 'pending': return 'Oczekujace';
    case 'in_progress': return 'W trakcie';
    case 'processing': return 'Przetwarzanie';
    case 'cancelled': return 'Anulowane';
    case 'rejected': return 'Odrzucone';
    default: return status || 'Nieznany';
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatCurrency = (amount) => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: 'PLN',
  }).format(amount);
};

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientMessages, setClientMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(true);

  useEffect(() => {
    fetchClient();
  }, [clientId]);

  useEffect(() => {
    if (clientId) {
      axios.get(`${API}/messages/client/${clientId}`)
        .then(res => setClientMessages(res.data.messages || []))
        .catch(() => {})
        .finally(() => setLoadingMessages(false));
    }
  }, [clientId]);

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API}/clients/${clientId}`);
      setClient(response.data);
    } catch (error) {
      toast.error('Nie udalo sie zaladowac danych klienta'); {/* t('clients.detail.loadError') */}
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!client) {
    return null;
  }

  const selections = client.selections || [];
  const orders = client.orders || [];

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/clients" />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-10">
        {/* Back Link */}
        <Link
          to="/clients"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Powrot do listy {/* t('clients.detail.backToList') */}
        </Link>

        {/* Client Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="bg-card border-border mb-8">
            <CardContent className="p-6">
              <div className="flex items-start gap-5">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-primary">
                    {client.name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl font-bold mb-3">{client.name}</h1>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {client.email && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="w-4 h-4 flex-shrink-0" />
                        <a href={`mailto:${client.email}`} className="hover:text-foreground transition-colors truncate">
                          {client.email}
                        </a>
                      </div>
                    )}
                    {client.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        <a href={`tel:${client.phone}`} className="hover:text-foreground transition-colors">
                          {client.phone}
                        </a>
                      </div>
                    )}
                  </div>
                  {client.notes && (
                    <div className="mt-4 flex items-start gap-2 text-sm text-muted-foreground">
                      <StickyNote className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <p className="whitespace-pre-wrap">{client.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Selections Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="mb-8"
        >
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Selekcje {/* t('clients.detail.selections') */}
            <span className="text-sm font-normal text-muted-foreground">
              ({selections.length})
            </span>
          </h2>

          {selections.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selections.map((selection, idx) => (
                <motion.div
                  key={selection.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: idx * 0.05 }}
                >
                  <Card className="bg-card border-border hover:border-primary/30 transition-all">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold truncate">
                            {selection.gallery?.name || selection.gallery_name || 'Galeria'}
                          </h3>
                        </div>
                        <Badge variant={statusBadgeVariant(selection.status)}>
                          {statusLabel(selection.status)}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(selection.created_at)}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Images className="w-3.5 h-3.5" />
                          {selection.photo_count ?? selection.photos?.length ?? 0} zdjec {/* t('clients.detail.photos') */}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <Card className="bg-card border-border">
              <CardContent className="p-8 text-center">
                <ImageIcon className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Brak selekcji dla tego klienta {/* t('clients.detail.noSelections') */}
                </p>
              </CardContent>
            </Card>
          )}
        </motion.div>

        {/* Orders Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Package className="w-5 h-5" />
            Zamowienia {/* t('clients.detail.orders') */}
            <span className="text-sm font-normal text-muted-foreground">
              ({orders.length})
            </span>
          </h2>

          {orders.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {orders.map((order, idx) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: idx * 0.05 }}
                >
                  <Card className="bg-card border-border hover:border-primary/30 transition-all">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Hash className="w-4 h-4 text-muted-foreground" />
                            <h3 className="font-semibold">
                              {order.order_number || `#${order.id}`}
                            </h3>
                          </div>
                        </div>
                        <Badge variant={statusBadgeVariant(order.status)}>
                          {statusLabel(order.status)}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(order.created_at)}
                        </span>
                        <span className="font-semibold">
                          {formatCurrency(order.total_amount)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <Card className="bg-card border-border">
              <CardContent className="p-8 text-center">
                <Package className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Brak zamowien dla tego klienta {/* t('clients.detail.noOrders') */}
                </p>
              </CardContent>
            </Card>
          )}
        </motion.div>

        {/* Messages Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="mt-8"
        >
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Wiadomosci
            <span className="text-sm font-normal text-muted-foreground">
              ({clientMessages.length})
            </span>
          </h2>

          {loadingMessages ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : clientMessages.length > 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="p-5 space-y-3">
                {clientMessages.slice(-5).map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[75%] rounded-xl px-4 py-2.5 ${
                      msg.direction === 'outbound'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary text-foreground'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.body_text}</p>
                      <p className={`text-[10px] mt-1 ${
                        msg.direction === 'outbound' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                      }`}>
                        {new Date(msg.created_at).toLocaleString('pl-PL', {
                          day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                ))}
                <div className="text-center pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/messages?client=${clientId}`)}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {clientMessages.length > 5 ? 'Zobacz caly watek' : 'Napisz wiadomosc'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-card border-border">
              <CardContent className="p-8 text-center">
                <MessageSquare className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-3">
                  Brak wiadomosci z tym klientem
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/messages?client=${clientId}`)}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Napisz wiadomosc
                </Button>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </main>
    </div>
  );
}
